import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, CircleNotch, Compass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { timeAgo } from "@/lib/format";

const STATE_META = {
  "Challenge Intake": { color: "hsl(var(--muted-foreground))", label: "Draft" },
  "Representation Audit": { color: "hsl(var(--tag-causal))", label: "Analyzing" },
  "CEO Confirmation": { color: "hsl(var(--tag-preference))", label: "Problem Framed" },
  "Advisory Council": { color: "hsl(var(--tag-interpretation))", label: "In Progress" },
};

const StateBadge = ({ state }) => {
  const meta = STATE_META[state] || {};
  const c = meta.color || "hsl(var(--muted-foreground))";
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]"
      style={{ color: c, borderColor: c }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
      {meta.label || state}
    </span>
  );
};

export default function Dashboard() {
  const [sessions, setSessions] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    api.listSessions().then(setSessions).catch(() => setSessions([]));
  }, []);

  return (
    <div className="w-full px-8 py-12">
      <div className="mb-10 border-b border-border pb-8">
        <div>
          <h1 className="font-serif text-4xl leading-[1.05] sm:text-5xl">
            Managing complexity, made visible.
          </h1>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
            A hard challenge is never one problem — it's a system of interlocking problems. The Room maps that system
            and composes a systemic solution that resolves several at once. Three steps:
          </p>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {[
              ["01", "Frame the problem", "You describe it; the Room represents it faithfully."],
              ["02", "Map the problem system", "Categories of interlocking problems, and the tensions between them."],
              ["03", "Explore systemic solutions", "Explore a systemic solution to your systemic problem — and if it doesn't fit, generate more."],
            ].map(([n, t, d]) => (
              <div key={n}>
                <div className="font-mono text-[11px] text-muted-foreground">{n}</div>
                <div className="mt-1 font-serif text-lg leading-tight">{t}</div>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
          <Link to="/demo" data-testid="dashboard-howitworks-btn" className="mt-6 inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2 text-xs font-medium transition-colors hover:bg-muted">
            <Compass size={14} /> How it works — take the sample tour
          </Link>
        </div>
      </div>

      {sessions !== null && sessions.length > 0 && (
        <div className="overline mb-4">Your challenge sessions</div>
      )}

      {sessions === null ? (
        <div className="flex items-center gap-2 py-16 text-muted-foreground">
          <CircleNotch size={18} className="animate-spin" /> Loading sessions…
        </div>
      ) : sessions.length === 0 ? (
        <div data-testid="empty-state" className="border border-dashed border-border py-20 text-center">
          <FileText size={28} className="mx-auto mb-4 text-muted-foreground" />
          <p className="font-serif text-2xl">No challenges yet</p>
          <p className="mx-auto mt-2 max-w-sm text-sm text-muted-foreground">
            Begin by describing an adaptive challenge as you currently understand it.
          </p>
        </div>
      ) : (
        <div data-testid="sessions-table" className="border border-border">
          <div className="hidden grid-cols-[1fr_200px_240px_120px] gap-4 border-b border-border bg-muted px-5 py-3 md:grid">
            <span className="overline">Title</span>
            <span className="overline">State</span>
            <span className="overline">Status</span>
            <span className="overline text-right">Updated</span>
          </div>
          {sessions.map((s) => (
            <button
              key={s.id}
              data-testid={`session-row-${s.id}`}
              onClick={() => nav(`/sessions/${s.id}`)}
              className="grid w-full grid-cols-1 gap-2 border-b border-border px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-muted md:grid-cols-[1fr_200px_240px_120px] md:items-center md:gap-4"
            >
              <span className="font-serif text-lg leading-tight">{s.title}</span>
              <span><StateBadge state={s.state} /></span>
              <span className="text-sm text-muted-foreground">{s.status}</span>
              <span className="font-mono text-xs text-muted-foreground md:text-right">{timeAgo(s.updated_at)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
